'use client';

import { useState, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';

interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
  correctAnswer: string; // The text of the correct answer
  explanation?: string;
}

interface ModuleQuizProps {
  content: string; // The markdown content containing the quiz
}

export function ModuleQuiz({ content }: ModuleQuizProps) {
  // Parse quiz from markdown
  const quiz = useMemo(() => {
    const questions: QuizQuestion[] = [];

    // Simplistic parser for the specific format in index.md
    // Format:
    // 1. **Question text**
    //    - [ ] Option 1
    //    - [ ] Option 2
    //
    //    **Answer**: Explanation text or correct option

    // Find the Assessments section
    const assessmentMatch = content.match(/## Assessments([\s\S]*?)(?:##|$)/);
    if (!assessmentMatch) return null;

    const assessmentText = assessmentMatch[1];

    // Split by numbered questions e.g., "1. "
    const questionBlocks = assessmentText.split(/\n\d+\.\s+\*\*/);

    questionBlocks.forEach((block, index) => {
      // Skip the first split if empty (before the first question)
      if (index === 0 && !block.trim()) return;

      // Re-add the "**" that was consumed by split if it wasn't the first empty block
      const fullBlock = index > 0 ? '**' + block : block;

      // Extract Question
      const qMatch = fullBlock.match(/\*\*([^*]+)\*\*/);
      if (!qMatch) return;
      const questionText = qMatch[1].trim();

      // Extract Options
      const options: string[] = [];
      const optionMatches = fullBlock.matchAll(/-\s+\[\s?\]\s+(.+)/g);
      for (const match of optionMatches) {
        options.push(match[1].trim());
      }

      // Extract Answer/Explanation
      const answerMatch = fullBlock.match(/\*\*Answer\*\*:\s*(.+)/);
      const explanation = answerMatch ? answerMatch[1].trim() : '';

      // Determine correct option based on explanation (simple fuzzy match)
      // This works if the explanation starts with the correct option text or contains specific keywords
      // For now, let's assume the explanation *starts* with the correct option text (as seen in the file: "Leading with your conclusion...")
      let correctOption = '';
      if (explanation) {
        // Try exact match first
        correctOption =
          options.find((opt) => explanation.startsWith(opt) || explanation.includes(opt)) || '';

        // Fallback: If no match, maybe the explanation repeats the answer text
        if (!correctOption && options.length > 0) {
          // Heuristic: The correct answer often shares the most bigrams with the start of the explanation
          // For simplicity, we might default to the option that is contained in the explanation
        }
      }

      if (questionText && options.length > 0) {
        questions.push({
          id: index,
          question: questionText,
          options,
          correctAnswer: correctOption, // Needs refinement if parsing is strict
          explanation,
        });
      }
    });

    return questions.length > 0 ? questions : null;
  }, [content]);

  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState(false);

  if (!quiz) return null;

  const handleSelect = (qId: number, option: string) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [qId]: option }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const handleReset = () => {
    setAnswers({});
    setSubmitted(false);
  };

  const score = quiz.reduce((acc, q) => {
    // Loose matching: strict parse might fail, so we check if the selected answer is "included" in explanation or matches identified logic
    // In the sample file, the explanation repeats the full answer text.
    // e.g. Answer: "Leading with your conclusion..." matches Option "Leading with your conclusion..."
    const isCorrect = q.explanation?.startsWith(answers[q.id]) || false;
    return acc + (isCorrect ? 1 : 0);
  }, 0);

  return (
    <div className="mt-8 pt-8 border-t border-border animate-in fade-in slide-in-from-bottom-4">
      <h2 className="text-2xl font-bold mb-6">Knowledge Check</h2>

      <div className="space-y-8">
        {quiz.map((q) => {
          const selected = answers[q.id];
          const isCorrect = submitted && q.explanation?.startsWith(selected);
          const isWrong = submitted && selected && !isCorrect;

          return (
            <div key={q.id} className="space-y-4">
              <h3 className="text-lg font-medium">
                {q.id}. {q.question}
              </h3>
              <div className="space-y-2">
                {q.options.map((opt) => {
                  const isSelected = selected === opt;
                  let optionClass = 'w-full justify-start text-left h-auto py-3 px-4';
                  let variant: 'outline' | 'default' | 'secondary' = 'outline';

                  if (submitted) {
                    if (q.explanation?.startsWith(opt)) {
                      optionClass += ' border-green-500 bg-green-50 dark:bg-green-900/20';
                      // Show checkmark
                    } else if (isSelected) {
                      optionClass += ' border-red-500 bg-red-50 dark:bg-red-900/20';
                    }
                  } else {
                    if (isSelected) {
                      variant = 'default';
                    }
                  }

                  return (
                    <Button
                      key={opt}
                      variant={variant}
                      className={optionClass}
                      onClick={() => handleSelect(q.id, opt)}
                      disabled={submitted}
                    >
                      <div className="flex items-center gap-2 w-full">
                        {/* Marker */}
                        <div
                          className={cn(
                            'w-4 h-4 rounded-full border shrink-0 flex items-center justify-center',
                            isSelected ? 'border-current' : 'border-muted-foreground'
                          )}
                        >
                          {isSelected && <div className="w-2 h-2 rounded-full bg-current" />}
                        </div>
                        <span className="flex-1 whitespace-normal">{opt}</span>
                        {submitted && q.explanation?.startsWith(opt) && (
                          <CheckCircle2 className="h-4 w-4 text-green-600 ml-2 shrink-0" />
                        )}
                        {submitted && isSelected && !q.explanation?.startsWith(opt) && (
                          <XCircle className="h-4 w-4 text-red-600 ml-2 shrink-0" />
                        )}
                      </div>
                    </Button>
                  );
                })}
              </div>
              {submitted && (
                <div className="bg-muted p-4 rounded-lg text-sm">
                  <span className="font-semibold block mb-1">Explanation:</span>
                  {q.explanation}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-center gap-4">
        {!submitted ? (
          <Button
            onClick={handleSubmit}
            disabled={Object.keys(answers).length < quiz.length}
            size="lg"
          >
            Check Answers
          </Button>
        ) : (
          <div className="flex items-center gap-4 w-full justify-between bg-muted/30 p-4 rounded-lg border border-border">
            <div className="flex flex-col">
              <span className="font-semibold text-lg">
                You scored {score} / {quiz.length}
              </span>
              <span className="text-muted-foreground text-sm">
                {score === quiz.length
                  ? "Perfect score! You've mastered this concept."
                  : 'Review the explanations to strengthen your understanding.'}
              </span>
            </div>
            <Button variant="outline" onClick={handleReset} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
